export const blogPosts = [
  {
    slug: 'why-top-performers-leave',
    featured: true,
    category: 'Research',
    title: 'Why Top Performers Leave — and How AI Predicts It',
    seoTitle: 'Why Top Performers Leave: AI Flight Risk & Retention | Stavyn Journal',
    metaDescription:
      'Learn why high performers quit and how external flight risk signals—salary, market demand, and hiring trends—help AI predict turnover months before resignation.',
    datePublished: '2026-02-18',
    excerpt:
      "High performers don't quit jobs, they quit environments. Understanding the external signals that precede departure can change how companies invest in retention.",
    author: 'Team Stavyn',
    authorTitle: 'Stavyn',
    date: 'February 18, 2026',
    readTime: '7 min read',
    body: [
      {
        type: 'paragraph',
        text: "There is a moment — usually invisible to leadership — when a high performer decides to leave. It doesn't announce itself. There is no dramatic confrontation, no formal warning. There is simply a quiet internal shift: the decision is made, the LinkedIn profile is refreshed, and the job search begins.",
      },
      {
        type: 'paragraph',
        text: "By the time a resignation letter lands on a manager's desk, the employee has usually been gone for months — emotionally, strategically, and in terms of full engagement. The tragedy isn't the departure itself. The tragedy is that the departure was, in most cases, preventable.",
      },
      {
        type: 'pullquote',
        text: 'By the time someone admits they\'re unhappy in a survey, they\'ve already updated their LinkedIn.',
      },
      {
        type: 'paragraph',
        text: "Traditional retention tools — engagement surveys, annual reviews, one-on-ones — operate on the employee's timeline, not the market's. They capture what people say when asked, not what they feel when no one is watching.",
      },
      {
        type: 'paragraph',
        text: "Stavyn takes a different approach. Instead of asking employees how they feel, we look at the external signals that precede a decision to leave: salary compression relative to market benchmarks, rising demand for their skill set at competing firms, lateral hiring trends in their industry, and changes in the competitive hiring landscape around their specific role.",
      },
      {
        type: 'pullquote',
        text: 'Risk signals emerge 3–6 months before resignation. That\'s the window leaders need to act.',
      },
      {
        type: 'paragraph',
        text: "These signals don't lie. When a senior engineer with your company sees a 22% salary premium available at three competitors — and those competitors are actively hiring their peers — the likelihood of departure rises dramatically. That rise happens long before any survey captures it.",
      },
      {
        type: 'paragraph',
        text: "Our 47-factor predictive model processes these external market signals in real time and maps them to individual employee profiles. The result is a clear, actionable risk score — not a vague sentiment index, but a specific probability tied to specific, addressable conditions.",
      },
      {
        type: 'paragraph',
        text: "For companies that care about their people, this kind of foresight is transformative. It doesn't just prevent turnover — it changes the conversation leaders have with their teams. Instead of reacting to resignations, you're proactively investing in the people who matter most.",
      },
    ],
  },
  {
    slug: 'hidden-signals-flight-risk',
    featured: false,
    category: 'Analytics',
    title: 'The Hidden Signals of Employee Flight Risk',
    seoTitle: 'Hidden Employee Flight Risk Signals HR Leaders Miss | Stavyn Journal',
    metaDescription:
      'Flight risk is driven by market salary drift, competitor hiring, and role demand—not just engagement surveys. See the external signals that predict voluntary turnover.',
    datePublished: '2026-01-30',
    excerpt:
      "Flight risk isn't random. Specific external patterns reliably precede departure — and most of them have nothing to do with how the employee feels about their manager.",
    author: 'Team Stavyn',
    authorTitle: 'Stavyn',
    date: 'January 30, 2026',
    readTime: '5 min read',
    body: [
      {
        type: 'paragraph',
        text: "When we talk about flight risk, it's tempting to focus on the usual suspects: a bad manager, a lack of growth, or burnout. Those matter. But the data tells a different story about why people actually leave — and when.",
      },
      {
        type: 'paragraph',
        text: "The strongest predictors of voluntary turnover are often external. Salary drift relative to the market. A spike in job postings for the same role at competing companies. A shift in hiring activity in the employee's skill set or geography. None of these show up in an engagement survey.",
      },
      {
        type: 'pullquote',
        text: "Flight risk isn't a feeling. It's a set of conditions that make leaving rational.",
      },
      {
        type: 'paragraph',
        text: "That doesn't mean internal factors are irrelevant. It means they're only part of the picture. An employee who feels underpaid and sees five similar roles paying 20% more elsewhere is at higher risk than someone who feels underpaid but has no clear external option. Context changes everything.",
      },
      {
        type: 'paragraph',
        text: "Stavyn's model weights both: internal context (tenure, role, performance signals) and external context (market salary, competitor hiring, demand for their skills). The result is a risk score that reflects real-world conditions, not just sentiment — and that gives leaders a concrete list of levers to pull before it's too late.",
      },
    ],
  },
  {
    slug: 'market-salary-data-retention',
    featured: false,
    category: 'Strategy',
    title: 'How Market Salary Data Changes Retention Strategy',
    seoTitle: 'Market Salary Data for Employee Retention Strategy | Stavyn Journal',
    metaDescription:
      'Salary compression and real-time market benchmarks are among the strongest predictors of turnover. How live compensation data changes retention strategy.',
    datePublished: '2026-01-12',
    excerpt:
      "Salary compression is one of the most reliable predictors of voluntary turnover. Here's how real-time market data transforms what companies can do about it.",
    author: 'Team Stavyn',
    authorTitle: 'Stavyn',
    date: 'January 12, 2026',
    readTime: '6 min read',
    body: [
      {
        type: 'paragraph',
        text: "Salary compression — when your pay slips behind the market while your people stay — is one of the steadiest drivers of voluntary turnover. Everyone knows it. The problem has never been awareness. It's been timing and data.",
      },
      {
        type: 'paragraph',
        text: "Historically, companies relied on annual compensation reviews and benchmark surveys that were months old by the time they landed. By then, the market had often moved again. You were always reacting, never getting ahead.",
      },
      {
        type: 'pullquote',
        text: "Real-time market data turns compensation from a once-a-year event into a continuous conversation.",
      },
      {
        type: 'paragraph',
        text: "When you have access to live salary benchmarks by role, seniority, and geography, you can see exactly where each person stands relative to the market. You can spot compression before it becomes a resignation. You can make targeted adjustments — raises, title changes, retention bonuses — instead of blanket increases that miss the people at highest risk.",
      },
      {
        type: 'paragraph',
        text: "That shift doesn't just reduce turnover. It changes how HR and managers talk about pay. The conversation moves from 'we do a review once a year' to 'we're making sure you're aligned with the market.' It's the same budget, used more intelligently — and with far better outcomes for retention.",
      },
    ],
  },
  {
    slug: 'predictive-hr-next-evolution',
    featured: false,
    category: 'Industry',
    title: 'Predictive HR: The Next Evolution of People Analytics',
    seoTitle: 'Predictive HR & People Analytics: The Next Evolution | Stavyn Journal',
    metaDescription:
      'People analytics is moving from descriptive reporting to predictive flight risk. How HR teams can act on turnover probability before resignations.',
    datePublished: '2025-12-20',
    excerpt:
      'People analytics has moved from descriptive to diagnostic. The frontier now is predictive — understanding what will happen before it does.',
    author: 'Team Stavyn',
    authorTitle: 'Stavyn',
    date: 'December 20, 2025',
    readTime: '4 min read',
    body: [
      {
        type: 'paragraph',
        text: "People analytics has come a long way. We went from counting heads and tracking tenure to understanding engagement, performance, and attrition after the fact. That's descriptive and diagnostic. Useful — but reactive.",
      },
      {
        type: 'paragraph',
        text: "The next step is predictive: understanding who is at risk of leaving before they hand in their notice. Not guessing. Not surveying. Using external market data and internal signals to estimate probability and prioritize action.",
      },
      {
        type: 'pullquote',
        text: "Predictive HR isn't about replacing judgment. It's about giving leaders a clearer picture so they can act earlier.",
      },
      {
        type: 'paragraph',
        text: "That shift is already happening in sales (pipeline forecasting), marketing (churn prediction), and operations. HR has lagged in part because the data was messier and the outcomes harder to tie to a single lever. But with better data on market salary, competitor hiring, and role-level demand, the models are finally good enough to be actionable — and the ROI of keeping the right people is too large to ignore.",
      },
    ],
  },
  {
    slug: 'why-exit-interviews-are-too-late',
    featured: false,
    category: 'Opinion',
    title: 'Why Exit Interviews Are Too Late',
    seoTitle: 'Why Exit Interviews Are Too Late for Employee Retention | Stavyn Journal',
    metaDescription:
      'Exit interviews explain past turnover but rarely prevent the next departure. Why early flight risk signals beat reactive retention conversations.',
    datePublished: '2025-11-28',
    excerpt:
      "Exit interviews tell you why someone left. They do nothing to help you keep the next person. It's time to move the conversation earlier.",
    author: 'Team Stavyn',
    authorTitle: 'Stavyn',
    date: 'November 28, 2025',
    readTime: '4 min read',
    body: [
      {
        type: 'paragraph',
        text: "Exit interviews have a noble goal: learn why people left so you can do better next time. The trouble is, by the time someone is in that room, they're already gone. The next person at risk might be sitting at their desk right now, and the exit interview won't help them at all.",
      },
      {
        type: 'paragraph',
        text: "What you learn in an exit interview is still valuable — process gaps, culture issues, compensation complaints. But it's backward-looking. It tells you what went wrong for one person. It doesn't tell you who else might leave for similar reasons, or what to do before they make the same decision.",
      },
      {
        type: 'pullquote',
        text: "The best time to have the retention conversation is before someone has decided to leave.",
      },
      {
        type: 'paragraph',
        text: "That's why we built Stavyn around early signals: market salary movement, competitor hiring, demand for specific skills. When you know who is at risk and why — compensation, opportunity, or market pull — you can have the right conversation at the right time. Not in an exit interview. In a one-on-one, with a raise or a new project or a clear path forward. That's how you keep people, instead of just learning why you lost them.",
      },
    ],
  },
  {
    slug: 'economics-of-employee-retention',
    featured: false,
    category: 'Research',
    title: 'The Economics of Employee Retention',
    seoTitle: 'Employee Retention ROI & the Cost of Turnover | Stavyn Journal',
    metaDescription:
      'Replacing talent costs more than targeted retention. Understand turnover economics, retention ROI, and when proactive investment pays off.',
    datePublished: '2025-11-05',
    excerpt:
      'The cost of turnover is higher than most leaders realize — and the ROI of retention is larger than most tools can justify. The math has finally changed.',
    author: 'Team Stavyn',
    authorTitle: 'Stavyn',
    date: 'November 5, 2025',
    readTime: '8 min read',
    body: [
      {
        type: 'paragraph',
        text: "Replacing an employee is expensive. Recruiting, onboarding, lost productivity, and institutional knowledge add up fast — often to one or two times annual salary for mid-level roles, and far more for senior or specialized talent. Most leaders know this. What they don't always see is how much more cost-effective it is to prevent the departure in the first place.",
      },
      {
        type: 'paragraph',
        text: "The economics of retention are straightforward: a targeted raise or retention bonus is almost always cheaper than a full replacement. The challenge has been knowing who to invest in, and when. Without good data on who is at risk and why, companies either over-invest (blanket raises) or under-invest (wait until someone leaves, then scramble).",
      },
      {
        type: 'pullquote',
        text: "Retention isn't a cost. It's an investment with a clear ROI when you target the right people.",
      },
      {
        type: 'paragraph',
        text: "When you can identify flight risk early — using market salary data, competitor hiring activity, and role-level demand — you can make smaller, smarter interventions. A 10% adjustment for someone who is 20% behind market is far less than the cost of replacing them. A conversation about growth or a new project can sometimes be enough. The key is acting before the decision to leave is final.",
      },
      {
        type: 'paragraph',
        text: "We built Stavyn so that the math is visible. You see who is at risk, why, and what levers you have. That makes it easier to justify retention spend to finance, and easier for managers to prioritize their time. The economics of employee retention only work when you have the data to act on. Now you do.",
      },
    ],
  },
];

export const featuredPost = blogPosts.find(p => p.featured);
export const gridPosts = blogPosts.filter(p => !p.featured);
